import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import moment from 'moment-timezone'
import { Daycare, DaycareType } from '@/types/Daycare'
import { Role } from '@/types/Role'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import styles from './daycareProfile.module.scss'
import profileStyles from '../DaycareProfileHandler.module.scss'
import { SET_DAYCARE_TIMEZONE } from '@/modules/API/daycare/daycareHandler'
import Title from '@/components/_custom/Title/title'
import { Edit } from '@mui/icons-material'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import UserInput from '@/components/_custom/UserInput/userInput'
import EditDaycareProfile from './Dialog/editDaycareProfileDialog'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import EditOpeningHours from './Dialog/editOpeningHoursDialog'
import NewOpeningHours from './Dialog/newOpeningHoursDialog'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { daycareTypeToString } from '@/modules/helper/daycareHelper'
import CountrySelect from '@/components/_custom/CountrySelect/countrySelect'
import { useAuth } from '@/modules/context/AuthContext'
import Mobile from '@/modules/desktopMobile/mobile'

interface DaycareProfileProps {
    daycare: Daycare
    role: Role
}

const DaycareProfile: FunctionComponent<DaycareProfileProps> = ({ daycare, role }: DaycareProfileProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const [isEditDaycareProfileOpen, setEditDaycareProfileOpen] = useState<boolean>(false)

    const [noDaycareOpeningsHours, setNoDaycareOpeningsHours] = useState<boolean>(
        daycare.opening.monOpen === null &&
            daycare.opening.monClose === null &&
            daycare.opening.tueOpen === null &&
            daycare.opening.tueClose === null &&
            daycare.opening.wedOpen === null &&
            daycare.opening.wedClose === null &&
            daycare.opening.thuOpen === null &&
            daycare.opening.thuClose === null &&
            daycare.opening.friOpen === null &&
            daycare.opening.friClose === null &&
            daycare.opening.satOpen === null &&
            daycare.opening.satClose === null &&
            daycare.opening.sunOpen === null &&
            daycare.opening.sunClose === null
    )
    const [isNewOpeningHoursOpen, setNewOpeningHoursOpen] = useState<boolean>(false)

    const [timezone, setTimezone] = useState<string>(daycare.timezone?.valueOf() || '')

    const [daycareName, setDaycareName] = useState<string>(daycare.name.valueOf())
    const [phone, setPhone] = useState<string>(daycare.phone.valueOf())
    const [type, setType] = useState<DaycareType>(daycare.type)
    const [managerName, setManagerName] = useState<string>(daycare.manager.valueOf())
    const [email, setEmail] = useState<string>(daycare.email.valueOf())
    const [address, setAddress] = useState<string>(daycare.address.valueOf())
    const [zip, setZip] = useState<string>(daycare.zip.valueOf())
    const [city, setCity] = useState<string>(daycare.city.valueOf())
    const [country, setCountry] = useState<string>(daycare.country.valueOf())

    const [setDaycareTimezone] = useMutation(SET_DAYCARE_TIMEZONE)

    const timezones: SelectOptionsDeprecated = [
        {
            options: moment.tz.names().map((zone: string): SelectOptionDeprecated => {
                const offset: number = moment.tz(zone).utcOffset() / 60
                return {
                    name: `(GMT${offset < 0 ? offset : `+${offset}`}) ${zone}`,
                    value: zone
                }
            })
        }
    ]

    const [monOpen, setMonOpen] = useState<null | string>(
        daycare.opening.monOpen !== null ? moment(daycare.opening.monOpen, 'HH:mm').format('HH:mm') : null
    )
    const [monClose, setMonClose] = useState<null | string>(
        daycare.opening.monClose !== null ? moment(daycare.opening.monClose, 'HH:mm').format('HH:mm') : null
    )
    const [monBreakStart, setMonBreakStart] = useState<null | string>(
        daycare.opening.monBreakStart !== null ? moment(daycare.opening.monBreakStart, 'HH:mm').format('HH:mm') : null
    )
    const [monBreakEnd, setMonBreakEnd] = useState<null | string>(
        daycare.opening.monBreakEnd !== null ? moment(daycare.opening.monBreakEnd, 'HH:mm').format('HH:mm') : null
    )
    const [tueOpen, setTueOpen] = useState<null | string>(
        daycare.opening.tueOpen !== null ? moment(daycare.opening.tueOpen, 'HH:mm').format('HH:mm') : null
    )
    const [tueClose, setTueClose] = useState<null | string>(
        daycare.opening.tueClose !== null ? moment(daycare.opening.tueClose, 'HH:mm').format('HH:mm') : null
    )
    const [tueBreakStart, setTueBreakStart] = useState<null | string>(
        daycare.opening.tueBreakStart !== null ? moment(daycare.opening.tueBreakStart, 'HH:mm').format('HH:mm') : null
    )
    const [tueBreakEnd, setTueBreakEnd] = useState<null | string>(
        daycare.opening.tueBreakEnd !== null ? moment(daycare.opening.tueBreakEnd, 'HH:mm').format('HH:mm') : null
    )
    const [wedOpen, setWedOpen] = useState<null | string>(
        daycare.opening.wedOpen !== null ? moment(daycare.opening.wedOpen, 'HH:mm').format('HH:mm') : null
    )
    const [wedClose, setWedClose] = useState<null | string>(
        daycare.opening.wedClose !== null ? moment(daycare.opening.wedClose, 'HH:mm').format('HH:mm') : null
    )
    const [wedBreakStart, setWedBreakStart] = useState<null | string>(
        daycare.opening.wedBreakStart !== null ? moment(daycare.opening.wedBreakStart, 'HH:mm').format('HH:mm') : null
    )
    const [wedBreakEnd, setWedBreakEnd] = useState<null | string>(
        daycare.opening.wedBreakEnd !== null ? moment(daycare.opening.wedBreakEnd, 'HH:mm').format('HH:mm') : null
    )
    const [thuOpen, setThuOpen] = useState<null | string>(
        daycare.opening.thuOpen !== null ? moment(daycare.opening.thuOpen, 'HH:mm').format('HH:mm') : null
    )
    const [thuClose, setThuClose] = useState<null | string>(
        daycare.opening.thuClose !== null ? moment(daycare.opening.thuClose, 'HH:mm').format('HH:mm') : null
    )
    const [thuBreakStart, setThuBreakStart] = useState<null | string>(
        daycare.opening.thuBreakStart !== null ? moment(daycare.opening.thuBreakStart, 'HH:mm').format('HH:mm') : null
    )
    const [thuBreakEnd, setThuBreakEnd] = useState<null | string>(
        daycare.opening.thuBreakEnd !== null ? moment(daycare.opening.thuBreakEnd, 'HH:mm').format('HH:mm') : null
    )
    const [friOpen, setFriOpen] = useState<null | string>(
        daycare.opening.friOpen !== null ? moment(daycare.opening.friOpen, 'HH:mm').format('HH:mm') : null
    )
    const [friClose, setFriClose] = useState<null | string>(
        daycare.opening.friClose !== null ? moment(daycare.opening.friClose, 'HH:mm').format('HH:mm') : null
    )
    const [friBreakStart, setFriBreakStart] = useState<null | string>(
        daycare.opening.friBreakStart !== null ? moment(daycare.opening.friBreakStart, 'HH:mm').format('HH:mm') : null
    )
    const [friBreakEnd, setFriBreakEnd] = useState<null | string>(
        daycare.opening.friBreakEnd !== null ? moment(daycare.opening.friBreakEnd, 'HH:mm').format('HH:mm') : null
    )
    const [satOpen, setSatOpen] = useState<null | string>(
        daycare.opening.satOpen !== null ? moment(daycare.opening.satOpen, 'HH:mm').format('HH:mm') : null
    )
    const [satClose, setSatClose] = useState<null | string>(
        daycare.opening.satClose !== null ? moment(daycare.opening.satClose, 'HH:mm').format('HH:mm') : null
    )
    const [satBreakStart, setSatBreakStart] = useState<null | string>(
        daycare.opening.satBreakStart !== null ? moment(daycare.opening.satBreakStart, 'HH:mm').format('HH:mm') : null
    )
    const [satBreakEnd, setSatBreakEnd] = useState<null | string>(
        daycare.opening.satBreakEnd !== null ? moment(daycare.opening.satBreakEnd, 'HH:mm').format('HH:mm') : null
    )
    const [sunOpen, setSunOpen] = useState<null | string>(
        daycare.opening.sunOpen !== null ? moment(daycare.opening.sunOpen, 'HH:mm').format('HH:mm') : null
    )
    const [sunClose, setSunClose] = useState<null | string>(
        daycare.opening.sunClose !== null ? moment(daycare.opening.sunClose, 'HH:mm').format('HH:mm') : null
    )
    const [sunBreakStart, setSunBreakStart] = useState<null | string>(
        daycare.opening.sunBreakStart !== null ? moment(daycare.opening.sunBreakStart, 'HH:mm').format('HH:mm') : null
    )
    const [sunBreakEnd, setSunBreakEnd] = useState<null | string>(
        daycare.opening.sunBreakEnd !== null ? moment(daycare.opening.sunBreakEnd, 'HH:mm').format('HH:mm') : null
    )

    useEffect(() => {
        if (timezone && daycare.timezone !== timezone) {
            setDaycareTimezone({
                variables: {
                    daycare_id: daycare.id,
                    timezone: timezone
                }
            })
        }
    }, [timezone])

    async function closeNewOpeningHours(): Promise<void> {
        setNewOpeningHoursOpen(false)
        setNoDaycareOpeningsHours(false)
    }

    const canUpdate: boolean = !!user?.currentDaycareRights.canWriteParamsDaycare

    useEffect(() => {
        if (daycare.opening) {
            setMonOpen(
                daycare.opening.monOpen !== null ? moment(daycare.opening.monOpen, 'HH:mm').format('HH:mm') : null
            )
            setMonClose(
                daycare.opening.monClose !== null ? moment(daycare.opening.monClose, 'HH:mm').format('HH:mm') : null
            )
            setMonBreakStart(
                daycare.opening.monBreakStart !== null
                    ? moment(daycare.opening.monBreakStart, 'HH:mm').format('HH:mm')
                    : null
            )
            setMonBreakEnd(
                daycare.opening.monBreakEnd !== null
                    ? moment(daycare.opening.monBreakEnd, 'HH:mm').format('HH:mm')
                    : null
            )
            setTueOpen(
                daycare.opening.tueOpen !== null ? moment(daycare.opening.tueOpen, 'HH:mm').format('HH:mm') : null
            )
            setTueClose(
                daycare.opening.tueClose !== null ? moment(daycare.opening.tueClose, 'HH:mm').format('HH:mm') : null
            )
            setTueBreakStart(
                daycare.opening.tueBreakStart !== null
                    ? moment(daycare.opening.tueBreakStart, 'HH:mm').format('HH:mm')
                    : null
            )
            setTueBreakEnd(
                daycare.opening.tueBreakEnd !== null
                    ? moment(daycare.opening.tueBreakEnd, 'HH:mm').format('HH:mm')
                    : null
            )
            setWedOpen(
                daycare.opening.wedOpen !== null ? moment(daycare.opening.wedOpen, 'HH:mm').format('HH:mm') : null
            )
            setWedClose(
                daycare.opening.wedClose !== null ? moment(daycare.opening.wedClose, 'HH:mm').format('HH:mm') : null
            )
            setWedBreakStart(
                daycare.opening.wedBreakStart !== null
                    ? moment(daycare.opening.wedBreakStart, 'HH:mm').format('HH:mm')
                    : null
            )
            setWedBreakEnd(
                daycare.opening.wedBreakEnd !== null
                    ? moment(daycare.opening.wedBreakEnd, 'HH:mm').format('HH:mm')
                    : null
            )
            setThuOpen(
                daycare.opening.thuOpen !== null ? moment(daycare.opening.thuOpen, 'HH:mm').format('HH:mm') : null
            )
            setThuClose(
                daycare.opening.thuClose !== null ? moment(daycare.opening.thuClose, 'HH:mm').format('HH:mm') : null
            )
            setThuBreakStart(
                daycare.opening.thuBreakStart !== null
                    ? moment(daycare.opening.thuBreakStart, 'HH:mm').format('HH:mm')
                    : null
            )
            setThuBreakEnd(
                daycare.opening.thuBreakEnd !== null
                    ? moment(daycare.opening.thuBreakEnd, 'HH:mm').format('HH:mm')
                    : null
            )
            setFriOpen(
                daycare.opening.friOpen !== null ? moment(daycare.opening.friOpen, 'HH:mm').format('HH:mm') : null
            )
            setFriClose(
                daycare.opening.friClose !== null ? moment(daycare.opening.friClose, 'HH:mm').format('HH:mm') : null
            )
            setFriBreakStart(
                daycare.opening.friBreakStart !== null
                    ? moment(daycare.opening.friBreakStart, 'HH:mm').format('HH:mm')
                    : null
            )
            setFriBreakEnd(
                daycare.opening.friBreakEnd !== null
                    ? moment(daycare.opening.friBreakEnd, 'HH:mm').format('HH:mm')
                    : null
            )
            setSatOpen(
                daycare.opening.satOpen !== null ? moment(daycare.opening.satOpen, 'HH:mm').format('HH:mm') : null
            )
            setSatClose(
                daycare.opening.satClose !== null ? moment(daycare.opening.satClose, 'HH:mm').format('HH:mm') : null
            )
            setSatBreakStart(
                daycare.opening.satBreakStart !== null
                    ? moment(daycare.opening.satBreakStart, 'HH:mm').format('HH:mm')
                    : null
            )
            setSatBreakEnd(
                daycare.opening.satBreakEnd !== null
                    ? moment(daycare.opening.satBreakEnd, 'HH:mm').format('HH:mm')
                    : null
            )
            setSunOpen(
                daycare.opening.sunOpen !== null ? moment(daycare.opening.sunOpen, 'HH:mm').format('HH:mm') : null
            )
            setSunClose(
                daycare.opening.sunClose !== null ? moment(daycare.opening.sunClose, 'HH:mm').format('HH:mm') : null
            )
        }
    }, [daycare, daycare.opening])

    useEffect(() => {
        if (daycare) {
            setDaycareName(daycare.name.valueOf())
            setPhone(daycare.phone.valueOf())
            setType(daycare.type)
            setManagerName(daycare.manager.valueOf())
            setEmail(daycare.email.valueOf())
            setAddress(daycare.address.valueOf())
            setCity(daycare.city.valueOf())
            setZip(daycare.zip.valueOf())
            setCountry(daycare.country.valueOf())
            setTimezone(daycare.timezone?.valueOf() || '')
            setNoDaycareOpeningsHours(
                daycare.opening.monOpen === null &&
                    daycare.opening.monClose === null &&
                    daycare.opening.tueOpen === null &&
                    daycare.opening.tueClose === null &&
                    daycare.opening.wedOpen === null &&
                    daycare.opening.wedClose === null &&
                    daycare.opening.thuOpen === null &&
                    daycare.opening.thuClose === null &&
                    daycare.opening.friOpen === null &&
                    daycare.opening.friClose === null &&
                    daycare.opening.satOpen === null &&
                    daycare.opening.satClose === null &&
                    daycare.opening.sunOpen === null &&
                    daycare.opening.sunClose === null
            )
        }
    }, [daycare])

    return (
        <div className={profileStyles.PagePosition}>
            <div className={styles.ProfilPlacement}>
                <Mobile>
                    {user && user.currentDaycare?.logoBase64Data && (
                        <img
                            src={`data:image/png;base64,${user.currentDaycare.logoBase64Data}`}
                            alt={`${daycare?.name} logo`}
                            className={styles.LogoDaycare}
                        />
                    )}
                </Mobile>
                <div className={styles.Form}>
                    <div className={styles.TitleAndButton}>
                        <div className={styles.Title}>
                            <Title text={t('daycareInformation')} />
                        </div>
                        {canUpdate && (
                            <ButtonTextIcon
                                type={'edit'}
                                onClick={() => setEditDaycareProfileOpen(true)}
                                disabled={isEditDaycareProfileOpen}
                            />
                        )}
                    </div>
                    <div className={styles.InputContainer}>
                        <div className={styles.Input}>
                            <UserInput
                                label={t('daycareType')}
                                value={daycareTypeToString(type, t)}
                                className={styles.UserInput}
                                type={'text'}
                                readOnly
                            />
                            <UserInput
                                label={t('daycareName')}
                                value={daycareName}
                                className={styles.UserInput}
                                type={'text'}
                                format={'pascal'}
                                readOnly
                            />
                            <UserInput
                                label={t('managerName')}
                                value={managerName}
                                className={styles.UserInput}
                                type={'text'}
                                format={'pascal'}
                                readOnly
                            />
                        </div>
                        <div className={styles.Input}>
                            <UserInput
                                label={t('phone')}
                                value={phone}
                                className={styles.UserInput}
                                readOnly
                                type='phone'
                            />
                            <UserInput
                                label={t('email')}
                                value={email}
                                className={styles.UserInput}
                                readOnly
                                type='email'
                            />
                        </div>
                    </div>
                    <div className={styles.InputContainer}>
                        <div className={styles.Input}>
                            <UserInput
                                label={t('address')}
                                value={address}
                                className={styles.UserInput}
                                type={'text'}
                                readOnly
                            />
                            <UserInput
                                label={t('city')}
                                value={city}
                                className={styles.UserInput}
                                type={'text'}
                                format={'pascal'}
                                readOnly
                            />
                        </div>
                        <div className={styles.Input}>
                            <UserInput
                                label={t('zipCode')}
                                value={zip}
                                className={styles.UserInput}
                                type={'text'}
                                format={'pascal'}
                                readOnly
                            />
                            <div className={styles.UserInput}>
                                <CountrySelect country={country} readOnly />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.ProfilPlacement}>
                <div className={styles.Form}>
                    <div className={styles.TitleAndButton}>
                        <div className={styles.Title}>
                            <Title text={t('openingHours')} />
                        </div>
                    </div>
                    {noDaycareOpeningsHours ? (
                        <>
                            <p className={styles.Text}>{t('noDaycareOpeningsHours')}</p>
                            <ButtonTextIcon type={'add'} onClick={() => setNewOpeningHoursOpen(true)} />
                        </>
                    ) : (
                        <TableContainer className={styles.TablePlacement}>
                            <Table className={styles.Table}>
                                <TableHead className={styles.TableHeader}>
                                    <TableRow>
                                        <TableCell align={'center'}>{t('day')}</TableCell>
                                        <TableCell align={'center'}>{t('hours')}</TableCell>
                                        {canUpdate && <TableCell align={'center'}>{t('actions')}</TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <OpeningHoursRow
                                        daycareId={daycare.id}
                                        day={t('monday')}
                                        open={monOpen}
                                        close={monClose}
                                        canUpdate={canUpdate}
                                        breakStart={monBreakStart}
                                        breakEnd={monBreakEnd}
                                        setOpen={setMonOpen}
                                        setClose={setMonClose}
                                        setBreakStart={setMonBreakStart}
                                        setBreakEnd={setMonBreakEnd}
                                    />
                                    <OpeningHoursRow
                                        daycareId={daycare.id}
                                        day={t('tuesday')}
                                        open={tueOpen}
                                        close={tueClose}
                                        canUpdate={canUpdate}
                                        breakStart={tueBreakStart}
                                        breakEnd={tueBreakEnd}
                                        setOpen={setTueOpen}
                                        setClose={setTueClose}
                                        setBreakStart={setTueBreakStart}
                                        setBreakEnd={setTueBreakEnd}
                                    />
                                    <OpeningHoursRow
                                        daycareId={daycare.id}
                                        day={t('wednesday')}
                                        open={wedOpen}
                                        close={wedClose}
                                        canUpdate={canUpdate}
                                        breakStart={wedBreakStart}
                                        breakEnd={wedBreakEnd}
                                        setOpen={setWedOpen}
                                        setClose={setWedClose}
                                        setBreakStart={setWedBreakStart}
                                        setBreakEnd={setWedBreakEnd}
                                    />
                                    <OpeningHoursRow
                                        daycareId={daycare.id}
                                        day={t('thursday')}
                                        open={thuOpen}
                                        close={thuClose}
                                        canUpdate={canUpdate}
                                        breakStart={thuBreakStart}
                                        breakEnd={thuBreakEnd}
                                        setOpen={setThuOpen}
                                        setClose={setThuClose}
                                        setBreakStart={setThuBreakStart}
                                        setBreakEnd={setThuBreakEnd}
                                    />
                                    <OpeningHoursRow
                                        daycareId={daycare.id}
                                        day={t('friday')}
                                        open={friOpen}
                                        close={friClose}
                                        canUpdate={canUpdate}
                                        breakStart={friBreakStart}
                                        breakEnd={friBreakEnd}
                                        setOpen={setFriOpen}
                                        setClose={setFriClose}
                                        setBreakStart={setFriBreakStart}
                                        setBreakEnd={setFriBreakEnd}
                                    />
                                    <OpeningHoursRow
                                        daycareId={daycare.id}
                                        day={t('saturday')}
                                        open={satOpen}
                                        close={satClose}
                                        canUpdate={canUpdate}
                                        breakStart={satBreakStart}
                                        breakEnd={satBreakEnd}
                                        setOpen={setSatOpen}
                                        setClose={setSatClose}
                                        setBreakStart={setSatBreakStart}
                                        setBreakEnd={setSatBreakEnd}
                                    />
                                    <OpeningHoursRow
                                        daycareId={daycare.id}
                                        day={t('sunday')}
                                        open={sunOpen}
                                        close={sunClose}
                                        canUpdate={canUpdate}
                                        breakStart={sunBreakStart}
                                        breakEnd={sunBreakEnd}
                                        setOpen={setSunOpen}
                                        setClose={setSunClose}
                                        setBreakStart={setSunBreakStart}
                                        setBreakEnd={setSunBreakEnd}
                                    />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </div>
            </div>
            {role === Role.cald && (
                <>
                    <div className={styles.ProfilPlacement}>
                        <div className={styles.Form}>
                            <div className={styles.TitleAndButton}>
                                <div className={styles.Title}>
                                    <Title text={t('timezone')} />
                                </div>
                            </div>
                            <div className={styles.InputContainer}>
                                <SelectUnique
                                    selectOptions={timezones}
                                    selectValue={timezone}
                                    setSelectValue={setTimezone}
                                    label={t('timezone')}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
            <EditDaycareProfile
                open={isEditDaycareProfileOpen}
                handleClose={() => setEditDaycareProfileOpen(false)}
                daycareName={daycareName}
                daycareId={daycare.id}
                setDaycareName={setDaycareName}
                phone={phone}
                setPhone={setPhone}
                type={type}
                setType={setType}
                managerName={managerName}
                setManagerName={setManagerName}
                email={email}
                setEmail={setEmail}
                address={address}
                setAddress={setAddress}
                city={city}
                setCity={setCity}
                zip={zip}
                setZip={setZip}
                country={country}
                setCountry={setCountry}
            />
            <NewOpeningHours open={isNewOpeningHoursOpen} handleClose={closeNewOpeningHours} daycareId={daycare.id} />
        </div>
    )
}

export default DaycareProfile

interface OpeningHoursRowProps {
    daycareId: number
    day: string
    open: string | null
    setOpen: (value: string | null) => void
    close: string | null
    setClose: (value: string | null) => void
    breakStart: string | null
    setBreakStart: (value: string | null) => void
    breakEnd: string | null
    setBreakEnd: (value: string | null) => void
    canUpdate: boolean
}

const OpeningHoursRow: FunctionComponent<OpeningHoursRowProps> = ({
    daycareId,
    day,
    open,
    setOpen,
    close,
    setClose,
    breakStart,
    setBreakStart,
    breakEnd,
    setBreakEnd,
    canUpdate
}: OpeningHoursRowProps) => {
    const { t } = useTranslation()

    const [isEditOpeningHoursOpen, setEditOpeningHoursOpen] = useState<boolean>(false)

    return (
        <>
            <TableRow>
                <TableCell align={'center'} className={styles.DayDaycareTitle}>
                    {day}
                </TableCell>
                <TableCell align={'center'}>
                    {open != null && close != null ? (
                        <>
                            {open}
                            {breakStart != null && breakEnd != null ? (
                                <>
                                    {` - ${breakStart}`}
                                    <br />
                                    {breakEnd}
                                </>
                            ) : null}
                            {` - ${close}`}
                        </>
                    ) : (
                        t('closed')
                    )}
                </TableCell>
                {canUpdate && (
                    <TableCell align={'center'} className={styles.Icon} onClick={() => setEditOpeningHoursOpen(true)}>
                        <Edit />
                    </TableCell>
                )}
            </TableRow>
            <EditOpeningHours
                open={isEditOpeningHoursOpen}
                handleClose={() => setEditOpeningHoursOpen(false)}
                day={day}
                openHours={open}
                setOpenHours={setOpen}
                closeHours={close}
                setCloseHours={setClose}
                breakStart={breakStart}
                setBreakStart={setBreakStart}
                breakEnd={breakEnd}
                setBreakEnd={setBreakEnd}
                daycareId={daycareId}
            />
        </>
    )
}
